import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { DocumentIcon } from '@heroicons/react/24/outline';

/**
 * Component to display the cover and interior PDFs for shipping
 */
const PDFShippingPreview = ({ bookId, onReady, savedPdfUrls, loading = false }) => {
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!savedPdfUrls) return;
    const { cover, verticalContent } = savedPdfUrls;

    // While generating, suppress any prior error
    if (loading) {
      setError(null);
      return;
    }

    if (cover && verticalContent) {
      setError(null);
      onReady({ coverPdfUrl: cover, interiorPdfUrl: verticalContent });
    } else if (!cover) {
      setError('Cover PDF is missing. Please generate it in the Print step.');
    } else if (!verticalContent) {
      setError('Interior PDF is missing. Please generate it in the Print step.');
    }
  }, [savedPdfUrls, onReady, loading]);

  return (
    <div className="w-full max-w-2xl mx-auto">

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-700">{error}</p>
          <p className="text-sm text-red-600 mt-1">
            Please go back to the Print step to generate all required PDFs.
          </p>
        </div>
      )}

      {loading && (
        <div className="mb-4 rounded-md bg-indigo-50 border border-indigo-200 p-4 text-indigo-700">
          <div className="flex items-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Generating PDFs for cover and interior...</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Cover PDF */}
        <div className="border rounded-lg p-4 bg-white shadow-sm">
          <div className="flex items-center mb-2">
            <DocumentIcon className="h-5 w-5 text-blue-500 mr-2" />
            <span className="font-medium">Cover PDF</span>
          </div>
          
          {loading || !savedPdfUrls?.cover ? (
            <div className="p-4 bg-gray-50 rounded text-gray-500 text-center">
              {loading ? 'Generating...' : 'Not generated'}
            </div>
          ) : (
            <div className="mt-2">
              <a 
                href={savedPdfUrls.cover} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 flex items-center"
              >
                <span className="mr-1">View Cover PDF</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          )}
        </div>

        {/* Interior PDF */}
        <div className="border rounded-lg p-4 bg-white shadow-sm">
          <div className="flex items-center mb-2">
            <DocumentIcon className="h-5 w-5 text-green-500 mr-2" />
            <span className="font-medium">Interior PDF</span>
          </div>
          
          {loading || !savedPdfUrls?.verticalContent ? (
            <div className="p-4 bg-gray-50 rounded text-gray-500 text-center">
              {loading ? 'Generating...' : 'Not generated'}
            </div>
          ) : (
            <div className="mt-2">
              <a 
                href={savedPdfUrls.verticalContent} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 flex items-center"
              >
                <span className="mr-1">View Interior PDF</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default PDFShippingPreview;
